/* eslint-disable react-hooks/exhaustive-deps */
import { setChatContext } from "../../lib/slices/chat.slice";
import { useAppDispatch, useAppSelector } from "../../lib/store";
import { AnimatePresence, motion } from "framer-motion";
import { Phone } from "lucide-react";
import { FC, useEffect, useRef, useState } from "react";
import { useGetAgentWidgetConfigQuery } from "../../apis/agent.api";
import {
  useCreateChatContextMutation,
  useGetChatMessagesQuery,
  useSendChatMessageMutation,
} from "../../apis/chat.api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import MessageBubble from "../../components/ui/message-bubble";
import { WidgetScene } from "../types/widget-scenes";
import { iChoiceProps } from "./choice";
import WidgetHeader from "./widget-header";

const ChoiceChat: FC<iChoiceProps> = ({ setWidgetStage, agentId }) => {
  const [input, setInput] = useState("");
  const [isChatVisible, setIsChatVisible] = useState(true);
  const chatRef = useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();
  const { chatId } = useAppSelector((state) => state.chat);
  const localChatId = localStorage.getItem(`chatId_${agentId}`);

  const [createChatContext, { isLoading: isCreatingContext }] =
    useCreateChatContextMutation();
  const {
    data: messages = [],
    isLoading: isLoadingMessages,
    error: getContextError,
  } = useGetChatMessagesQuery(chatId || localChatId || "", {
    skip: !chatId && !localChatId,
    refetchOnMountOrArgChange: true
  });
  const [sendMessage, { isLoading: isSendingMessage }] =
    useSendChatMessageMutation();
  const { data: widgetConfig } = useGetAgentWidgetConfigQuery(agentId);
  const isLoading = isCreatingContext || isLoadingMessages;

  useEffect(() => {
    const initChat = async () => {
      if ((getContextError as { status: number })?.status === 404) {
        // localStorage.removeItem(`chatId_${agentId}`);
        dispatch(setChatContext({ chatId: "", agentId }));
      }

      if (!chatId && !localChatId && agentId) {
        try {
          const result = await createChatContext({ agentId }).unwrap();
          localStorage.setItem(`chatId_${agentId}`, result.chatId);
          dispatch(setChatContext({ chatId: result.chatId, agentId }));
        } catch (error) {
          console.error("Failed to create chat context:", error);
        }
      }
    };

    initChat();
  }, [chatId, agentId, createChatContext, dispatch, getContextError]);

  useEffect(() => {
    if (chatRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  }, [messages, isSendingMessage]);

  const handleVoiceSelection = () => setWidgetStage(WidgetScene.CALL);

  const handleSend = async () => {
    if (!input.trim() || (!chatId && !localChatId)) return;
    setInput("");
    try {
      await sendMessage({
        contextId: chatId || localChatId || "",
        message: { message: input, messageType: "text" },
      }).unwrap();
    } catch (error) {
      console.error("Failed to send message:", error);
    }
  };

  return (
    <div className="bg-[#F7F7F7] w-[320px] p-1 md:w-[406px] rounded-[20px]">
      <WidgetHeader
        config={widgetConfig}
        className="w-[320px] md:w-[406px] text-[12px] md:text-[14px]"
        onClick={() => setIsChatVisible(!isChatVisible)}
      />

      <AnimatePresence>
        {isChatVisible && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="p-4 flex flex-col gap-4"
          >
            <div
              ref={chatRef}
              className="flex flex-col gap-2 max-h-[75vh] overflow-y-auto"
            >
              {(chatId || localChatId) &&
                messages.map((msg, i) => {
                  return (
                    <MessageBubble
                      key={msg.messageType + msg.sender + msg.message + i}
                      message={msg}
                    />
                  );
                })}
            </div>

            <div className="flex items-center gap-4">
              <div className="relative flex-1">
                <Input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  className="h-[49px] rounded-full bg-white pl-4 pr-16 shadow-none border-none outline-0 ring-0"
                  placeholder="Ask me anything..."
                  onKeyDown={(e) => e.key === "Enter" && handleSend()}
                  disabled={isLoading || (!chatId && !localChatId)}
                />
                <Button
                  size="sm"
                  className="absolute top-2 px-2 rounded-full right-3 text-[13px]"
                  onClick={handleSend}
                  disabled={
                    isLoading || (!chatId && !localChatId) || !input.trim()
                  }
                  isLoading={isSendingMessage}
                >
                  Send
                </Button>
              </div>
              <Button
                onClick={handleVoiceSelection}
                className="rounded-full bg-[#373737] border-none text-white"
                variant="outline"
                size="icon"
              >
                <Phone />
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ChoiceChat;
